import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import ImageWithFallback from './ImageWithFallback';
import ShareButton from './ShareButton';
import NewsConnections from './NewsConnections';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiCalendar, FiTag } = FiIcons;

const NewsModal = ({ article, isOpen, onClose }) => {
  React.useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  if (!isOpen || !article) return null;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('el-GR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const shareUrl = `${window.location.origin}/news/${article.id}`;
  const matchdayPost = Boolean(article.matchday_post_key);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={onClose} />

          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            transition={{ type: 'spring', duration: 0.5 }}
            className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={onClose} className="absolute top-4 right-4 z-20 p-2 bg-black/30 hover:bg-black/50 text-white rounded-full backdrop-blur-md transition-all duration-200 group">
              <SafeIcon icon={FiX} className="w-6 h-6 group-hover:scale-110 transition-transform" />
            </button>

            {matchdayPost ? (
              <div className="flex-shrink min-h-0 bg-slate-100 rounded-t-2xl overflow-auto border-b border-slate-200">
                <ImageWithFallback src={article.image || article.image_url} alt={article.title} className="block w-full max-h-[55vh] object-contain" />
              </div>
            ) : (
              <div className="relative h-64 sm:h-80 flex-shrink-0 bg-gray-900 rounded-t-2xl overflow-hidden">
                <ImageWithFallback src={article.image || article.image_url} alt={article.title} className="w-full h-full object-cover opacity-90" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 text-white">
                  <div className="flex flex-wrap items-center gap-3 mb-3 text-sm font-medium">
                    {article.category && <span className="bg-primary-600/90 backdrop-blur-md px-3 py-1 rounded-full shadow-sm flex items-center"><SafeIcon icon={FiTag} className="w-3 h-3 mr-1.5" />{article.category}</span>}
                    <span className="flex items-center bg-black/30 backdrop-blur-md px-3 py-1 rounded-full"><SafeIcon icon={FiCalendar} className="w-3 h-3 mr-1.5" />{formatDate(article.published_at)}</span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight drop-shadow-lg">{article.title}</h2>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-6 sm:p-8 custom-scrollbar">
              {matchdayPost && (
                <div className="mb-6">
                  <div className="flex flex-wrap items-center gap-3 mb-3 text-sm text-gray-500">
                    {article.category && <span className="bg-primary-100 text-primary-800 px-3 py-1 rounded-full font-semibold">{article.category}</span>}
                    <span>{formatDate(article.published_at)}</span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-black text-gray-900">{article.title}</h2>
                </div>
              )}

              <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap">{article.content}</div>

              {article.sources && article.sources.length > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-100">
                  <p className="text-sm text-gray-500 font-semibold mb-2">Πηγές:</p>
                  <div className="flex flex-wrap gap-2">{article.sources.map((source, idx) => <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full font-medium">{source}</span>)}</div>
                </div>
              )}

              <NewsConnections article={article} />
            </div>

            <div className="p-4 sm:p-6 bg-gray-50 border-t border-gray-100 flex justify-between items-center flex-shrink-0 rounded-b-2xl relative z-10">
              <ShareButton
                url={shareUrl}
                title={article.title}
                description={article.excerpt || (article.content ? article.content.substring(0, 100) + '...' : '')}
                imageUrl={article.image || article.image_url || ''}
                variant="outline"
                buttonText="Κοινοποίηση"
                direction="up"
                align="left"
                className="bg-white"
              />
              <button onClick={onClose} className="px-6 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl font-medium transition-colors">Κλείσιμο</button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default NewsModal;
